import React, { useState, useEffect, useCallback } from "react";
import {
  Bot, MessageSquare, BarChart3, AlertCircle, Hand, DollarSign,
  TrendingUp, Search, RefreshCw, Loader2, Zap, Clock,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { Form, Table } from "react-bootstrap";
import { PageHeader, AppCard, AppCardHeader, AppCardTitle, AppCardContent, AppButton, AppBadge, StatsCard, EmptyState } from "../../ui";
import apiClient from "../../../utils/apiClient";

const tooltipStyle = {
  backgroundColor: "var(--card-bg, #fff)",
  border: "1px solid var(--border-primary, #e5e7eb)",
  borderRadius: 8,
  fontSize: 12,
  padding: "8px 12px",
};

const monthOptions = (n = 12) => {
  const out = [];
  const d = new Date();
  for (let i = 0; i < n; i += 1) {
    const dt = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - i, 1));
    const ym = `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}`;
    out.push({ value: ym, label: dt.toLocaleDateString(undefined, { year: "numeric", month: "short" }) });
  }
  return out;
};

const SAAIWhatsAppUsagePage = () => {
  const [month, setMonth] = useState(monthOptions(1)[0].value);
  const [search, setSearch] = useState("");
  const [overview, setOverview] = useState(null);
  const [series, setSeries] = useState([]);
  const [hourly, setHourly] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [{ data: ov }, { data: ts }, { data: hr }] = await Promise.all([
        apiClient.get(`/sa/ai-whatsapp/overview?month=${month}`),
        apiClient.get(`/sa/ai-whatsapp/timeseries?months=12`),
        apiClient.get(`/sa/ai-whatsapp/hourly`),
      ]);
      setOverview(ov);
      setSeries(ts.months || []);
      setHourly(hr);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load AI WhatsApp analytics");
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    load();
    // Auto-refresh hourly card every 60s
    const t = setInterval(() => {
      apiClient.get(`/sa/ai-whatsapp/hourly`).then(({ data }) => setHourly(data)).catch(() => {});
    }, 60000);
    return () => clearInterval(t);
  }, [load]);

  const filtered = (overview?.institutes || []).filter((r) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (r.instituteName || "").toLowerCase().includes(q)
      || (r.instituteEmail || "").toLowerCase().includes(q);
  });

  const totals = overview?.totals || {};
  const adoption = overview?.adoption || {};

  return (
    <div className="sa-ai-whatsapp">
      <PageHeader
        title="AI WhatsApp — Platform Usage"
        subtitle="Cross-institute AI agent activity, cost, and quota tracking"
        icon={Bot}
        actions={(
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <Form.Select size="sm" value={month} onChange={(e) => setMonth(e.target.value)} style={{ minWidth: 140 }}>
              {monthOptions(12).map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </Form.Select>
            <AppButton size="sm" variant="outline" onClick={load} disabled={loading}>
              {loading ? <Loader2 size={14} className="spin" /> : <RefreshCw size={14} />} Refresh
            </AppButton>
          </div>
        )}
      />

      {error && (
        <div style={{ padding: 12, marginBottom: 12, background: "#fee2e2", color: "#991b1b", borderRadius: 6 }}>
          {error}
        </div>
      )}

      {/* Totals */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 20 }}>
        <StatsCard icon={Bot} label="Replies generated" value={totals.repliesGenerated || 0} />
        <StatsCard icon={MessageSquare} label="Replies sent" value={totals.repliesSent || 0} />
        <StatsCard icon={Hand} label="Handoffs" value={totals.handoffsTriggered || 0} />
        <StatsCard icon={DollarSign} label="Total cost (USD)" value={`$${(totals.totalCostUsd || 0).toFixed(4)}`} />
        <StatsCard icon={BarChart3} label="Tokens (in/out)" value={`${(totals.promptTokens || 0).toLocaleString()} / ${(totals.completionTokens || 0).toLocaleString()}`} />
      </div>

      {/* Adoption */}
      <AppCard style={{ marginBottom: 16 }}>
        <AppCardHeader>
          <AppCardTitle><TrendingUp size={16} /> Adoption</AppCardTitle>
        </AppCardHeader>
        <AppCardContent>
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            <div><strong>{adoption.totalInstitutesConfigured || 0}</strong> institutes configured</div>
            <div><strong>{adoption.activeAi || 0}</strong> with AI active (suggest/auto)</div>
            <div><strong>{adoption.leadFollowupActive || 0}</strong> using lead follow-up</div>
          </div>
        </AppCardContent>
      </AppCard>

      {/* Live (last hour) cost-cap utilisation */}
      {hourly && (
        <AppCard style={{ marginBottom: 16 }}>
          <AppCardHeader>
            <AppCardTitle><Zap size={16} /> Last hour — live spend & cap utilisation</AppCardTitle>
            <span style={{ fontSize: 11, color: "#666", display: "inline-flex", alignItems: "center", gap: 4 }}>
              <Clock size={11} /> auto-refresh every 60s
            </span>
          </AppCardHeader>
          <AppCardContent>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12, marginBottom: 12 }}>
              <StatsCard icon={MessageSquare} label="Replies (1h)" value={(hourly.totals?.replies || 0).toLocaleString()} />
              <StatsCard icon={DollarSign} label="Spend (1h)" value={`$${(hourly.totals?.costUsd || 0).toFixed(4)}`} />
              <StatsCard
                icon={AlertCircle}
                label="At cap"
                value={hourly.totals?.institutesAtCap || 0}
                iconColor={hourly.totals?.institutesAtCap > 0 ? "rose" : "blue"}
              />
              <StatsCard
                icon={AlertCircle}
                label="Near cap (≥80%)"
                value={hourly.totals?.institutesNearCap || 0}
                iconColor={hourly.totals?.institutesNearCap > 0 ? "amber" : "blue"}
              />
            </div>
            {(hourly.institutes || []).filter((r) => r.capLimit > 0).length === 0 ? (
              <div style={{ padding: 12, textAlign: "center", color: "#999", fontSize: 13 }}>
                No institutes have set an hourly cost cap yet.
              </div>
            ) : (
              <Table responsive hover size="sm" style={{ marginBottom: 0 }}>
                <thead>
                  <tr>
                    <th>Institute</th>
                    <th style={{ textAlign: "right" }}>Replies</th>
                    <th style={{ textAlign: "right" }}>Spend (1h)</th>
                    <th style={{ textAlign: "right" }}>Cap</th>
                    <th style={{ textAlign: "right" }}>Utilisation</th>
                  </tr>
                </thead>
                <tbody>
                  {hourly.institutes.filter((r) => r.capLimit > 0).map((r) => (
                    <tr key={r.instituteId}>
                      <td>
                        <div style={{ fontWeight: 500 }}>{r.instituteName}</div>
                        {r.instituteEmail && <div style={{ fontSize: 11, color: "#666" }}>{r.instituteEmail}</div>}
                      </td>
                      <td style={{ textAlign: "right" }}>{r.replies}</td>
                      <td style={{ textAlign: "right" }}>${r.costUsd.toFixed(4)}</td>
                      <td style={{ textAlign: "right" }}>${r.capLimit.toFixed(2)}</td>
                      <td style={{ textAlign: "right" }}>
                        <span style={{
                          color: r.capExceeded ? "#dc2626" : r.capUtilPct >= 80 ? "#d97706" : "inherit",
                          fontWeight: r.capExceeded ? 600 : 400,
                        }}>
                          {r.capUtilPct}%
                        </span>
                        {r.capExceeded && <AlertCircle size={12} color="#dc2626" style={{ marginLeft: 4 }} />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </AppCardContent>
        </AppCard>
      )}

      {/* Time series chart */}
      <AppCard style={{ marginBottom: 16 }}>
        <AppCardHeader>
          <AppCardTitle><BarChart3 size={16} /> Last 12 months</AppCardTitle>
        </AppCardHeader>
        <AppCardContent>
          {series.length === 0 ? (
            <div style={{ padding: 20, textAlign: "center", color: "var(--muted-foreground, #666)" }}>No data</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={series}>
                <defs>
                  <linearGradient id="grad-replies" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2563eb" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#2563eb" stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="grad-cost" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#059669" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#059669" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area yAxisId="left" type="monotone" dataKey="repliesSent" name="Replies sent" stroke="#2563eb" fill="url(#grad-replies)" />
                <Area yAxisId="right" type="monotone" dataKey="totalCostUsd" name="Cost (USD)" stroke="#059669" fill="url(#grad-cost)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </AppCardContent>
      </AppCard>

      {/* Per-institute table */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>Institutes — {month}</AppCardTitle>
          <div style={{ position: "relative" }}>
            <Search size={14} style={{ position: "absolute", left: 8, top: 9, color: "#999" }} />
            <Form.Control
              size="sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search institute..."
              style={{ paddingLeft: 28, width: 220 }}
            />
          </div>
        </AppCardHeader>
        <AppCardContent style={{ padding: 0 }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: "center" }}>
              <Loader2 size={24} className="spin" /> Loading...
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState icon={<Bot size={32} />} title="No usage" description="No institutes have generated AI replies in this period." />
          ) : (
            <Table responsive hover size="sm" style={{ marginBottom: 0 }}>
              <thead>
                <tr>
                  <th>Institute</th>
                  <th>Plan</th>
                  <th>Mode</th>
                  <th style={{ textAlign: "right" }}>Generated</th>
                  <th style={{ textAlign: "right" }}>Sent</th>
                  <th style={{ textAlign: "right" }}>Handoffs</th>
                  <th style={{ textAlign: "right" }}>Quota</th>
                  <th style={{ textAlign: "right" }}>Cost</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.instituteId}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{r.instituteName}</div>
                      {r.instituteEmail && (
                        <div style={{ fontSize: 11, color: "#666" }}>{r.instituteEmail}</div>
                      )}
                    </td>
                    <td>{r.planName || <span style={{ color: "#999" }}>—</span>}</td>
                    <td>
                      <AppBadge variant={r.mode === "auto" ? "success" : r.mode === "suggest" ? "warning" : "secondary"}>
                        {r.mode || "off"}
                      </AppBadge>
                      {r.leadFollowupEnabled && <AppBadge variant="outline" style={{ marginLeft: 4 }}>followup</AppBadge>}
                    </td>
                    <td style={{ textAlign: "right" }}>{r.repliesGenerated.toLocaleString()}</td>
                    <td style={{ textAlign: "right" }}>{r.repliesSent.toLocaleString()}</td>
                    <td style={{ textAlign: "right" }}>{r.handoffsTriggered.toLocaleString()}</td>
                    <td style={{ textAlign: "right" }}>
                      {r.quota === -1
                        ? <AppBadge variant="success">∞</AppBadge>
                        : r.quota === 0
                          ? <span style={{ color: "#999" }}>—</span>
                          : (
                            <span style={{ color: r.quotaPct >= 100 ? "#dc2626" : r.quotaPct >= 80 ? "#d97706" : "inherit" }}>
                              {r.repliesGenerated} / {r.quota} ({r.quotaPct}%)
                            </span>
                          )}
                      {r.quotaExhaustedAt && <AlertCircle size={12} color="#dc2626" style={{ marginLeft: 4 }} />}
                    </td>
                    <td style={{ textAlign: "right" }}>${r.totalCostUsd.toFixed(4)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </AppCardContent>
      </AppCard>
    </div>
  );
};

export default SAAIWhatsAppUsagePage;
